import React, { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-location'
import { useStore } from 'TourApp/store'
import { eversetBrand } from 'shared/settings'
import { events, posttour } from 'shared/analytics'

import ChipPicker from 'shared/components/ChipPicker'
import Button from './components/Button'
import RoomItem from './components/RoomItem'
import Accordion from './components/Accordion'

import { dataMapper } from './services/dataMapper'

// 0 - hide filter component
// 1 - show filter component and open first dropdown with options
// 2 - show filter component and open second dropdown with options
const FILTER_CODES = {
  HIDE: 0,
  SHOW_1: 1,
  SHOW_2: 2,
}

export default function WishListScreen() {
  const { selectedApartmentId, tourData } = useStore()
  const [roomFilter, setRoomFilter] = useState('All Rooms')
  const [brandFilter, setBrandFilter] = useState('All Brands')
  const [filterIndex, setFilterIndex] = useState(FILTER_CODES.HIDE)
  const navigate = useNavigate()
  const [loaded, setLoaded] = useState(false)
  const [data, setData] = useState({
    rooms: [],
    brands: [],
    roomDesigns: [],
  })

  useEffect(() => {
    const r = data.roomDesigns.find((r) => (r.room.name || r.room.classification) === roomFilter)
    posttour(events.wfra, () => ({
      new_room_id: r?.room.id || null,
      new_room_name: r?.room.name || r?.room.classification || null,
    }))
  }, [roomFilter])

  useEffect(() => {
    const b = data.roomDesigns.find((r) => r.main_vendor.full_name === brandFilter)
    posttour(events.wfva, () => ({
      new_vendor_id: b?.main_vendor.id || null,
      new_vendor_name: b?.main_vendor.full_name || null,
    }))
  }, [brandFilter])

  useEffect(() => {
    const _data = dataMapper(selectedApartmentId, tourData)
    setLoaded(true)
    if (_data) setData(_data)
  }, [tourData, selectedApartmentId])

  function getWishListEventProperties(item) {
    // Group_5
    const properties = {
      physical_room_id: item.room.id,
      physical_room_name: item.room.name || item.room.classification,
      design_id: item.id,
      design_name: item.design_name,
      vendor_id: item.main_vendor.id,
      vendor_name: item.main_vendor.full_name,
    }
    return properties
  }

  // Filter items by filter params
  const getItems = () => {
    return (data.roomDesigns || [])
      .filter((i) => roomFilter === data.rooms[0] || (i.room.name || i.room.classification) === roomFilter)
      .filter((i) => brandFilter === data.brands[0] || i.main_vendor.full_name === brandFilter)
  }

  function hasWishlist() {
    return Object.values(data).every(el=>el.length > 0)
  }

  return (
    loaded && (
      <>
        {hasWishlist() ? (
          <div className="w-full h-full overflow-x-auto pt-[24px] px-[16px] lg:px-[24px] lg:pt-[26px] relative">
            <ChipPicker
              items={data.rooms}
              picked={roomFilter}
              classNames="hidden lg:block mr-[-32px] w-[calc(100%+32px)]"
              activeItemClassNames='!bg-black'
              onPicked={setRoomFilter}
            />
            <ChipPicker
              items={data.brands}
              picked={brandFilter}
              classNames="hidden lg:block mt-[16px] mr-[-32px] w-[calc(100%+32px)]"
              activeItemClassNames='!bg-black'
              onPicked={setBrandFilter}
            />

            <div className="lg:hidden flex flex-row mb-[-8px]">
              <Button
                className="!h-[34px]"
                onClick={() =>
                  setFilterIndex(filterIndex === FILTER_CODES.HIDE ? FILTER_CODES.SHOW_1 : FILTER_CODES.HIDE)
                }
              >
                {roomFilter}
              </Button>
              <Button
                className="ml-[8px] !h-[34px]"
                onClick={() =>
                  setFilterIndex(filterIndex === FILTER_CODES.HIDE ? FILTER_CODES.SHOW_2 : FILTER_CODES.HIDE)
                }
              >
                {brandFilter}
              </Button>
            </div>

            {filterIndex !== FILTER_CODES.HIDE && (
              <div className="lg:hidden flex flex-col fixed z-20 bottom-0 left-0 right-0 bg-white">
                <div className="flex w-full justify-between items-center px-[16px] py-[14px] border-solid border-[#C7CCD9] border-b-[1px]">
                  <span className="font-medium text-[12px] leading-[15px] md:text-[14px] uppercase">All Filters</span>
                  <span
                    className="underline text-[14px] leading-[21px] md:text-[16px] font-semibold cursor-pointer"
                    onClick={() => setFilterIndex(FILTER_CODES.HIDE)}
                  >
                    Done
                  </span>
                </div>
                <Accordion
                  title="Rooms"
                  items={data.rooms}
                  active={roomFilter}
                  isOpen={filterIndex === FILTER_CODES.SHOW_1}
                  selectOption={(o) => {
                    setRoomFilter(o)
                    posttour(events.wfrc)
                  }}
                  toggle={() => setFilterIndex(FILTER_CODES.SHOW_1)}
                />
                <Accordion
                  title="Brands"
                  items={data.brands}
                  active={brandFilter}
                  isOpen={filterIndex === FILTER_CODES.SHOW_2}
                  selectOption={(o) => {
                    setBrandFilter(o)
                    posttour(events.wfvc)
                  }}
                  toggle={() => setFilterIndex(FILTER_CODES.SHOW_2)}
                />
              </div>
            )}
            {getItems().map((item, index, arr) => (
              <RoomItem
                room={item}
                isFirstItem={index === 0}
                isLastItem={index === arr.length - 1}
                key={index}
                onAssetCardLink={(asset) => {
                  posttour(events.wacl, () => ({
                    ...getWishListEventProperties(item),
                    product_link: asset.web_page,
                    affiliation_link: asset.affiliation_link,
                    asset_id: asset.id,
                    asset_name: asset.asset_name,
                  }))
                }}
                onPreviewEvent={(hover = false) => {
                  posttour(hover ? events.wph : events.wpc, () => getWishListEventProperties(item))
                }}
                onWebsiteClick={() => {
                  // If the design is by The Everset - show the button "add to cart" with the link reflecting the items in the wishlist
                  const isAddToCart = item.main_vendor.full_name === eversetBrand
                  posttour(isAddToCart ? events.wccl : events.wvwc, () => ({
                    ...getWishListEventProperties(item),
                    url: item.main_vendor.website_link,
                  }))
                }}
              />
            ))}
          </div>
        ) : (
          <>
            <div className=" h-full w-full flex items-center justify-center text-[22px] font-normal text-[#5F6787]">
              No items in Wishlist
            </div>
          </>
        )}
      </>
    )
  )
}
